import React, { useState } from 'react';
import { LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './ContactPage.module.css';

const supportEmail = 'support@throttlexshare.com';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  rentalType: '',
  tripLocation: '',
  subject: '',
  message: '',
  honeypot: '',
};

const ContactPage = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();

    setStatus('sending');
    setStatusMessage('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.message || 'Message could not be sent.');
      }

      setForm(initialForm);
      setStatus('success');
      setStatusMessage('Message sent. The ThrottleX team will review it soon.');
    } catch (e) {
      setStatus('error');
      setStatusMessage(
        e?.message ||
          'Message could not be sent right now. Please email support@throttlexshare.com directly.'
      );
    }
  };

  return (
    <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
      <main className={css.root}>
        <section className={css.hero}>
          <a href="/" className={css.backLink}>← Back to homepage</a>

          <p className={css.eyebrow}>Support</p>
          <h1>Contact ThrottleX</h1>
          <p className={css.intro}>
            Have a question about rentals, listings, deposits, pickup details, or using
            ThrottleX? Send us a message and our team will review it.
          </p>

          <div className={css.emailCard}>
            <span>Support email</span>
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          </div>
        </section>

        <section className={css.contactShell}>
          <form className={css.form} onSubmit={handleSubmit}>
            <div className={css.hiddenField}>
              <label>
                Leave this field empty
                <input
                  type="text"
                  name="honeypot"
                  value={form.honeypot}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </label>
            </div>

            <div className={css.formGrid}>
              <label>
                Name
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                />
              </label>

              <label>
                Email
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                />
              </label>

              <label>
                Phone
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Optional"
                />
              </label>

              <label>
                Rental type
                <select name="rentalType" value={form.rentalType} onChange={handleChange}>
                  <option value="">Select one</option>
                  <option value="Jet Ski">Jet Ski</option>
                  <option value="Boat">Boat</option>
                  <option value="UTV / Side-by-side">UTV / Side-by-side</option>
                  <option value="Owner listing question">Owner listing question</option>
                  <option value="General support">General support</option>
                </select>
              </label>
            </div>

            <label>
              Trip location
              <input
                name="tripLocation"
                value={form.tripLocation}
                onChange={handleChange}
                placeholder="Example: Lake Havasu, Las Vegas, Sand Hollow"
              />
            </label>

            <label>
              Subject
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="What can we help with?"
              />
            </label>

            <label>
              Message
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us what you need help with..."
                rows="7"
                required
              />
            </label>

            {statusMessage ? (
              <div
                className={`${css.statusMessage} ${
                  status === 'success' ? css.statusSuccess : css.statusError
                }`}
              >
                {statusMessage}
              </div>
            ) : null}

            <button type="submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending...' : 'Send message'}
            </button>

            <p className={css.directEmail}>
              You can also email us directly at{' '}
              <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
            </p>
          </form>

          <aside className={css.infoPanel}>
            <h2>What to include</h2>
            <ul>
              <li>Your rental type or listing question</li>
              <li>Your trip location or destination</li>
              <li>Your preferred rental date or timeframe</li>
              <li>Any pickup, deposit, damage, or safety questions</li>
            </ul>

            <div className={css.notice}>
              <strong>For urgent trip issues</strong>
              <p>
                Include your booking details, vehicle name, owner/renter name, and a
                clear description of the issue.
              </p>
            </div>
          </aside>
        </section>
      </main>
    </LayoutSingleColumn>
  );
};

export default ContactPage;
